import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button, GlassCard } from './ui';

const inputClassName =
  'w-full rounded-btn border border-white/10 bg-bg px-4 py-3 pr-10 text-sm text-body placeholder:text-muted/70 transition-colors focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/15';

const LicenseModal = ({ show, onHide, product, onSubmit, loading, existingRequests = [] }) => {
  const { t } = useTranslation();
  const [machineIdName, setMachineIdName] = useState('');
  const [error, setError] = useState('');
  const [validationState, setValidationState] = useState({ isValid: false, showValidation: false });

  const duplicateExists = (name) => {
    if (!Array.isArray(existingRequests)) return false;
    return existingRequests.some(r => r.product?._id === product?.productId && (r.machineIdName || '').trim().toLowerCase() === name.trim().toLowerCase());
  };

  const validateMachineIdFormat = (input) => {
    const trimmedInput = input.trim();
    
    // Vérifier si le champ est vide
    if (!trimmedInput) {
      return { isValid: false, error: t('profile.indicators.licenseModal.validation.required') };
    }
    
    // Vérifier la présence d'un tiret
    if (!trimmedInput.includes('-')) {
      return { 
        isValid: false, 
        error: t('profile.indicators.licenseModal.validation.missingDash')
      };
    }
    
    // Séparer les parties avant et après le tiret
    const parts = trimmedInput.split('-');
    
    // Vérifier qu'il y a exactement 2 parties (avant et après le premier tiret)
    if (parts.length < 2) {
      return { 
        isValid: false, 
        error: t('profile.indicators.licenseModal.validation.missingName')
      };
    }
    
    const machineIdPart = parts[0].trim();
    const namePart = parts.slice(1).join('-').trim(); // Rejoindre au cas où le nom contient des tirets
    
    // Vérifier que la partie Machine ID n'est pas vide
    if (!machineIdPart) {
      return { 
        isValid: false, 
        error: t('profile.indicators.licenseModal.validation.emptyMachineId')
      };
    }
    
    // Vérifier que la partie nom n'est pas vide
    if (!namePart) {
      return { 
        isValid: false, 
        error: t('profile.indicators.licenseModal.validation.emptyName')
      };
    }
    
    // Vérifier que le Machine ID a une longueur raisonnable (au moins 8 caractères)
    if (machineIdPart.length < 8) {
      return { 
        isValid: false, 
        error: t('profile.indicators.licenseModal.validation.shortMachineId')
      };
    }
    
    // Vérifier que le nom a au moins 2 caractères
    if (namePart.length < 2) {
      return { 
        isValid: false, 
        error: t('profile.indicators.licenseModal.validation.shortName')
      };
    }
    
    return { isValid: true, error: null };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation du format
    const validation = validateMachineIdFormat(machineIdName);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }

    if (duplicateExists(machineIdName)) {
      setError(t('profile.indicators.licenseModal.validation.duplicate'));
      return;
    }

    try {
      await onSubmit({
        machineIdName: machineIdName.trim()
      });
      
      // Réinitialiser le formulaire
      setMachineIdName('');
      setError('');
    } catch (err) {
      setError(err.message || 'Error during submission');
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setMachineIdName(value);
    setError(''); // Effacer l'erreur quand l'utilisateur tape
    
    // Validation en temps réel
    if (value.trim().length > 0) {
      const validation = validateMachineIdFormat(value);
      setValidationState({
        isValid: validation.isValid,
        showValidation: true
      });
    } else {
      setValidationState({ isValid: false, showValidation: false });
    }
  };

  const handleClose = () => {
    setMachineIdName('');
    setError('');
    setValidationState({ isValid: false, showValidation: false });
    onHide();
  };

  const inputBorderClass = validationState.showValidation
    ? validationState.isValid
      ? 'border-success focus:border-success focus:ring-success/40'
      : 'border-danger focus:border-danger focus:ring-danger/40'
    : '';

  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-bg/80 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative mx-4 w-full max-w-xl max-h-[90vh] overflow-y-auto"
          >
            <GlassCard hover={false} className="overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/[.08]">
                <h2 className="text-xl font-semibold text-body">
                  {t('profile.indicators.licenseModal.title')}
                </h2>
                <button
                  onClick={handleClose}
                  className="text-muted hover:text-body transition-colors"
                  type="button"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Product Info */}
                <div className="mb-6 p-4 rounded-btn border border-white/[.08] bg-white/[.03]">
                  <h3 className="font-semibold text-body mb-2">{product?.productName}</h3>
                  <p className="text-sm text-muted">
                    {t('profile.indicators.licenseModal.productInfo')}
                  </p>
                </div>

                {/* Instructions */}
                <div className="mb-6 rounded-btn border border-white/[.08] bg-bg/50 p-4">
                  <h4 className="mb-2 font-semibold text-body">
                    {t('profile.indicators.licenseModal.instructions.title')}
                  </h4>
                  <ol className="space-y-1 text-sm text-muted">
                    {t('profile.indicators.licenseModal.instructions.steps', { returnObjects: true }).map((step, index) => (
                      <li key={index}>
                        {index + 1}. {step.includes('Alturion') ? (
                          <>
                            {step.split('Alturion')[0]}
                            <code className="rounded-btn bg-surface px-2 py-1 text-body">Alturion</code>
                            {step.split('Alturion')[1] || ''}
                          </>
                        ) : step}
                        {index === 5 && (
                          <div className="mt-3 overflow-hidden rounded-btn border border-white/[.08] bg-white/[.03]">
                            <img
                              src="/images/Divers/3pLicensing.png"
                              alt={t('profile.indicators.licenseModal.instructions.screenshotAlt')}
                              className="max-h-72 w-full object-contain object-left"
                            />
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                  <p className="mt-2 text-xs text-muted">
                    {t('profile.indicators.licenseModal.instructions.warning')}
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-3 bg-danger/10 border border-danger/40 rounded-btn text-danger text-sm">
                      {error}
                    </div>
                  )}

                  <div>
                    <label htmlFor="machineIdName" className="block text-sm font-medium text-muted mb-2">
                      {t('profile.indicators.licenseModal.form.label')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="machineIdName"
                        value={machineIdName}
                        onChange={handleInputChange}
                        placeholder={t('profile.indicators.licenseModal.form.placeholder')}
                        className={`${inputClassName} ${inputBorderClass}`}
                        disabled={loading}
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs">
                        {validationState.showValidation ? (
                          validationState.isValid ? (
                            <span className="text-success">✓</span>
                          ) : (
                            <span className="text-danger">✕</span>
                          )
                        ) : (
                          <span className="text-primary">–</span>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-muted">
                        {t('profile.indicators.licenseModal.form.description')}
                      </p>
                      <p className="text-xs text-muted">
                        {t('profile.indicators.licenseModal.form.formatRequired')}
                      </p>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleClose}
                      className="flex-1"
                      disabled={loading}
                    >
                      {t('profile.indicators.licenseModal.form.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1"
                      disabled={loading}
                    >
                      {loading
                        ? t('profile.indicators.licenseModal.form.sending')
                        : t('profile.indicators.licenseModal.form.submit')}
                    </Button>
                  </div>
                </form>

                {/* Footer note */}
                <div className="mt-6 p-3 rounded-btn bg-white/[.03] border border-white/[.06]">
                  <p className="text-xs text-muted">
                    {t('profile.indicators.licenseModal.footer')}
                  </p>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default LicenseModal;
